import {
	Box,
	Button,
	Card,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Select,
	ThemeProvider,
	Tooltip,
	createTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { AdminPanelLabel, ConfigOrLabel } from "./types";
import LabelCard from "./LabelCard";
import CheckIcon from "@mui/icons-material/Check";
import "./styles.css";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import UndoIcon from "@mui/icons-material/Undo";
import { LabelOutputType, LabelType } from "../../context/ConfigContext/types";
import ConfigPanel from "./ConfigPanel";
import { grayscaleText } from "../../styles/theme";
import { PanelId, usePanelContext } from "../../context/PanelContext";
import OutsideAlerter from "../NavBar/OutsideAlerter";
import GenericExpandablePanel from "../GenericExpandablePanel";
import SearchTermPanel from "../SearchTermPanel/indes";

const ADD_TOPIC = "+ Add Topic";

const theme = createTheme({
	typography: {
		body2: {
			color: grayscaleText,
		},
	},
});

const TopicsPanel = () => {
	const {
		topics,
		handleTopics,
		selectedTopic,
		setSelectedTopic,
		handleLabels,
	} = useAdminPanelContext();
	const { expandedPanel, setExpandedPanel } = usePanelContext();
	const { setSelectedConfigId } = useAdminPanelContext();
	const [configOrLabel, setConfigOrLabel] = useState<ConfigOrLabel>(null);
	const [addTopic, setAddTopic] = useState(false);
	const [newTopicName, setNewTopicName] = useState("");
	const [editTopicNameMode, setEditTopicNameMode] = useState(false);
	const [editedTopicName, setEditedTopicName] = useState("");
	const [lastUnsavedId, setLastUnsavedId] = useState<number>(null);

	useEffect(() => {
		setSelectedConfigId(null);
		return () => {
			setSelectedConfigId(null);
		};
	}, [configOrLabel]);

	const handleTopicChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		if (event.target.value === ADD_TOPIC) {
			setConfigOrLabel(null);
			setSelectedTopic(null);
			return setAddTopic(true);
		}
		setNewTopicName("");
		setAddTopic(false);
		setSelectedTopic(
			topics.find((topic) => topic.name === event.target.value).id
		);
	};
	const handleCreateTopic = async () => {
		await handleTopics.add(newTopicName);
		setNewTopicName("");
		setAddTopic(false);
	};
	const handleUpdateTopic = async () => {
		// await handleTopics.update({ id: selectedTopic, name: editedTopicName });
		// setEditedTopicName("");
		// setEditTopicNameMode(false);
	};

	const undoEditTopicName = () => {
		setEditTopicNameMode(false);
		setEditedTopicName("");
	};

	const handleAddNewCard = () => {
		// if (configOrLabel === ConfigOrLabel.CONFIGS) {
		//   const unsavedConfig: ScraperConfig = {
		//     id: lastUnsavedId - 1,
		//     active: false,
		//     topicId: selectedTopic,
		//     scraper:
		//     scraperConfigOptions: null
		//   };
		//   handleScrapers.add(unsavedLabel);
		//   setLastUnsavedId(unsavedLabel.id);
		// }
		const unsavedLabel: AdminPanelLabel = {
			id: lastUnsavedId - 1,
			name: `New label ${Math.abs(lastUnsavedId - 1)}`,
			type: LabelType.CATEGORICAL,
			outputType: LabelOutputType.CATEGORY,
			active: true,
			possibleLabelValues: [],
		};
		handleLabels.add(unsavedLabel, false);
		setLastUnsavedId(unsavedLabel.id);
	};

	return (
		<Box>
			{!topics?.length ? (
				<LinearProgress />
			) : (
				<ThemeProvider theme={theme}>
					<Box component="div" sx={{ display: "inline" }}>
						{expandedPanel === PanelId.SearchTerm && (
							<OutsideAlerter
								onOutsideClick={() => setExpandedPanel(null)}
								excludedElementId={PanelId.SearchTerm}
							>
								<GenericExpandablePanel>
									<SearchTermPanel />
								</GenericExpandablePanel>
							</OutsideAlerter>
						)}
						<Box sx={{ marginTop: "20px" }}>
							<FormControl sx={{ m: 1, minWidth: 80, marginLeft: "64px" }}>
								<InputLabel>Topic</InputLabel>
								<Select
									displayEmpty
									value={topics.find((t) => t.id === selectedTopic)?.name || ""}
									onChange={(event: any) => handleTopicChange(event)}
									label="Select a topic"
								>
									<MenuItem key={0} value={ADD_TOPIC}>
										{ADD_TOPIC}
									</MenuItem>
									{topics.map((topic) => {
										return (
											<MenuItem key={topic.id} value={topic.name}>
												{topic.name}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>

							{selectedTopic ? (
								<Box component="div" sx={{ display: "inline" }}>
									<FormControl sx={{ m: 1, minWidth: 80 }}>
										<InputLabel>View</InputLabel>
										<Select
											displayEmpty
											value={configOrLabel || ""}
											onChange={(event: any) =>
												setConfigOrLabel(event.target.value)
											}
											label="View configs or labels"
										>
											<MenuItem value={ConfigOrLabel.LABELS}>
												{ConfigOrLabel.LABELS}
											</MenuItem>
											<MenuItem value={ConfigOrLabel.SCRAPER_CONFIGS}>
												{ConfigOrLabel.SCRAPER_CONFIGS}
											</MenuItem>
										</Select>
									</FormControl>

									<FormControl sx={{ m: 1 }}>
										{editTopicNameMode ? (
											<>
												<InputLabel>New topic name</InputLabel>
												<OutlinedInput
													id="input-topic-name"
													type="text"
													value={editedTopicName}
													onChange={(e) => setEditedTopicName(e.target.value)}
													endAdornment={
														<InputAdornment position="end">
															<Tooltip title="Undo">
																<Box>
																	<IconButton
																		edge="end"
																		onClick={undoEditTopicName}
																	>
																		<UndoIcon />
																	</IconButton>
																</Box>
															</Tooltip>
															<Tooltip title="Save topic">
																<Box>
																	<IconButton
																		disabled={
																			!editedTopicName ||
																			topics.some(
																				(topic) => topic.name === newTopicName
																			)
																		}
																		color="success"
																		edge="end"
																		onClick={handleUpdateTopic}
																	>
																		<CheckIcon />
																	</IconButton>
																</Box>
															</Tooltip>
														</InputAdornment>
													}
													label="New topic name"
												/>
											</>
										) : (
											<Button
												disabled
												onClick={() => setEditTopicNameMode(true)}
												variant="contained"
											>
												Edit topic name
											</Button>
										)}
									</FormControl>
								</Box>
							) : (
								<Box component="div" sx={{ display: "inline" }}>
									{addTopic ? (
										<FormControl
											error={topics.some(
												(topic) => topic.name === newTopicName
											)}
											sx={{ m: 1, minWidth: 80 }}
										>
											<InputLabel>New topic name</InputLabel>
											<OutlinedInput
												id="input-topic-name"
												type="text"
												value={newTopicName}
												onChange={(e) => setNewTopicName(e.target.value)}
												endAdornment={
													<InputAdornment position="end">
														<Tooltip title="Save topic">
															<Box>
																<IconButton
																	disabled={
																		!newTopicName ||
																		topics.some(
																			(topic) => topic.name === newTopicName
																		)
																	}
																	color="success"
																	edge="end"
																	onClick={handleCreateTopic}
																>
																	<CheckIcon />
																</IconButton>
															</Box>
														</Tooltip>
													</InputAdornment>
												}
												label="New topic name"
											/>
										</FormControl>
									) : null}
								</Box>
							)}

							{!configOrLabel ? null : (
								<>
									{configOrLabel === ConfigOrLabel.LABELS ? (
										<>
											<Box
												sx={{
													marginTop: "32px",
												}}
											>
												<Card
													sx={{
														display: "flex",
														justifyContent: "center",
														marginLeft: "64px",
														marginRight: "64px",
													}}
												>
													<Button fullWidth onClick={() => handleAddNewCard()}>
														+ Add new
													</Button>
												</Card>
											</Box>
											<Box
												sx={{
													marginTop: "32px",
													display: "flex",
													flexDirection: "column",
													gap: "32px",
													paddingBottom: "30px",
												}}
											>
												{topics
													.find((t) => t.id === selectedTopic)
													.labels.map((label) => (
														<LabelCard key={label.id} label={label} />
													))}
											</Box>
										</>
									) : (
										<ConfigPanel
											configs={
												topics.find((t) => t.id === selectedTopic)
													.scraperConfigs
											}
										/>
									)}
								</>
							)}
						</Box>
					</Box>
				</ThemeProvider>
			)}
		</Box>
	);
};

export default TopicsPanel;
